import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';

interface SftpTestResult {
  success: boolean;
  message?: string;
  fileCount?: number;
  error?: string;
}

interface SftpFile {
  name: string;
  size: number;
  modifyTime: number;
  path: string;
}

interface SftpListResult {
  success: boolean;
  files: SftpFile[];
  count: number;
}

interface SftpDownloadResult {
  success: boolean;
  fileName: string;
  data: any;
  format: string;
}

interface SftpSyncResult {
  success: boolean;
  fileName: string;
  recordCount: number;
}

class SftpCloudService {
  /**
   * Test SFTP connection with stored credentials
   */
  async testConnection(): Promise<SftpTestResult> {
    try {
      const testConnection = httpsCallable<void, SftpTestResult>(
        functions,
        'sftpTestConnection'
      );
      const result = await testConnection();
      return result.data;
    } catch (error) {
      console.error('SFTP test connection failed:', error);
      throw error;
    }
  }

  /**
   * List files in SFTP directory
   */
  async listFiles(path?: string): Promise<SftpFile[]> {
    try {
      const listFiles = httpsCallable<{ path?: string }, SftpListResult>(
        functions,
        'sftpListFiles'
      );
      const result = await listFiles({ path });
      return result.data.files;
    } catch (error) {
      console.error('SFTP list files failed:', error);
      throw error;
    }
  }

  /**
   * Download and parse a specific file
   */
  async downloadFile(fileName: string): Promise<any> {
    try {
      const downloadFile = httpsCallable<{ fileName: string }, SftpDownloadResult>(
        functions,
        'sftpDownloadFile'
      );
      const result = await downloadFile({ fileName });
      return result.data.data;
    } catch (error) {
      console.error('SFTP download file failed:', error);
      throw error;
    }
  }

  /**
   * Sync SFTP file to Firestore
   */
  async syncFileToFirestore(fileName: string): Promise<SftpSyncResult> {
    try {
      const syncFile = httpsCallable<{ fileName: string }, SftpSyncResult>(
        functions,
        'sftpSyncToFirestore'
      );
      const result = await syncFile({ fileName });
      return result.data;
    } catch (error) {
      console.error('SFTP sync to Firestore failed:', error);
      throw error;
    }
  }
}

export const sftpCloudService = new SftpCloudService();
